<template>
  <b-card class="shadow-sm h-100">
    <b-card-title
      class="mb-2 px-6 pt-6 d-flex justify-content-between align-items-start"
    >
      <div>
        <b-icon icon="box-seam" variant="primary" class="mr-2" />
        <span>Estoque crítico</span>
      </div>

      <span class="badge badge-light-primary font-weight-bold">
        {{ monitorados }} monitorados
      </span>
    </b-card-title>

    <b-card-body class="pl-6 pr-6 pb-4 pt-3">
      <template v-if="loading">
        <b-skeleton width="38%" height="36px" class="mb-4" />
        <b-skeleton width="60%" height="14px" class="mb-5" />
        <b-skeleton v-for="i in 3" :key="i" height="18px" class="mb-3" />
      </template>

      <template v-else>
        <div class="d-flex justify-content-between align-items-end mb-5">
          <div>
            <h2 class="display-4 text-primary mb-0 font-weight-bold">
              {{ destaqueValor }}
            </h2>
            <div class="text-muted font-weight-bold">
              {{ destaqueLabel }}
            </div>
          </div>

          <button
            type="button"
            class="btn btn-light btn-sm font-weight-bold text-primary"
            :disabled="!zerados"
            @click="abrirModalZerados"
          >
            Ver zerados
          </button>
        </div>

        <div class="d-flex justify-content-between mb-4">
          <span class="text-muted font-weight-bold">
            Abaixo do mínimo monitorado
          </span>
          <strong class="text-dark-75">{{ criticos }}</strong>
        </div>

        <ul v-if="listaResumo.length" class="list-unstyled m-0 p-0">
          <li
            v-for="item in listaResumo"
            :key="item.id"
            class="d-flex justify-content-between align-items-center mb-3"
          >
            <span class="text-dark-75 text-truncate pr-3">
              {{ item.descricao }}
            </span>
            <span class="text-muted font-weight-bold flex-shrink-0">
              {{ formatNumber(item.quantidade) }}
            </span>
          </li>
        </ul>

        <div v-else class="text-muted font-weight-bold">
          Estoque sob controle nos produtos monitorados.
        </div>
      </template>
    </b-card-body>

    <b-modal
      :id="modalId"
      title="Produtos com estoque zerado"
      size="lg"
      hide-footer
    >
      <div v-if="loadingZerados" class="py-6">
        <b-skeleton v-for="i in 8" :key="i" height="18px" class="mb-3" />
      </div>

      <div v-else-if="modalZeradosItens.length">
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>Produto</th>
                <th class="text-right">Estoque</th>
                <th class="text-right">Mínimo</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in modalZeradosItens"
                :key="item.id"
                class="produto-row"
                @click="abrirProduto(item)"
              >
                <td>{{ item.descricao }}</td>
                <td class="text-right font-weight-bold">
                  {{ formatNumber(item.quantidade) }}
                </td>
                <td class="text-right text-muted">
                  {{ formatNumber(item.estoque_minimo) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="d-flex justify-content-between align-items-center mt-4 flex-wrap"
        >
          <span class="text-muted mb-2">
            Exibindo {{ paginationFrom }}-{{ paginationTo }} de
            {{ zeradosTotal }} produtos.
          </span>
          <b-pagination
            v-if="zeradosTotal > zeradosPerPage"
            v-model="zeradosPage"
            :total-rows="zeradosTotal"
            :per-page="zeradosPerPage"
            size="sm"
            class="mb-0"
            @change="fetchZerados"
          />
        </div>
      </div>

      <div v-else class="text-center text-muted py-6">
        Nenhum produto com estoque zerado.
      </div>
    </b-modal>
  </b-card>
</template>

<script>
export default {
  name: "EstoqueCritico",
  props: {
    dados: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      loadingZerados: false,
      modalZeradosItens: [],
      zeradosPage: 1,
      zeradosPerPage: 10,
      zeradosTotal: 0,
      paginationFrom: 0,
      paginationTo: 0,
    };
  },
  computed: {
    modalId() {
      return `modal-estoque-zerado-${this._uid}`;
    },
    criticos() {
      return Number(this.dados?.criticos || 0);
    },
    zerados() {
      return Number(this.dados?.zerados || 0);
    },
    monitorados() {
      return Number(this.dados?.monitorados || 0);
    },
    itens() {
      return Array.isArray(this.dados?.itens) ? this.dados.itens : [];
    },
    zeradosItens() {
      if (Array.isArray(this.dados?.zerados_itens)) {
        return this.dados.zerados_itens;
      }

      return this.itens.filter((item) => Number(item?.quantidade || 0) <= 0);
    },
    listaResumo() {
      return this.zeradosItens.length
        ? this.zeradosItens.slice(0, 4)
        : this.itens;
    },
    destaqueValor() {
      return this.zerados || this.criticos;
    },
    destaqueLabel() {
      if (this.zerados) {
        return this.zerados === 1 ? "produto zerado" : "produtos zerados";
      }

      return this.criticos === 1 ? "produto no limite" : "produtos no limite";
    },
  },
  methods: {
    abrirModalZerados() {
      if (!this.zerados) return;

      this.$bvModal.show(this.modalId);
      this.fetchZerados(1);
    },
    async fetchZerados(page = this.zeradosPage) {
      try {
        this.loadingZerados = true;
        this.zeradosPage = Number(page) || 1;

        const { data } = await this.$http.post("/dashboard/estoque-zerado", {
          page: this.zeradosPage,
          per_page: this.zeradosPerPage,
        });

        this.modalZeradosItens = Array.isArray(data?.data) ? data.data : [];
        this.zeradosTotal = Number(data?.total || 0);
        this.zeradosPage = Number(data?.current_page || this.zeradosPage);
        this.zeradosPerPage = Number(data?.per_page || this.zeradosPerPage);
        this.paginationFrom = Number(data?.from || 0);
        this.paginationTo = Number(data?.to || 0);
      } finally {
        this.loadingZerados = false;
      }
    },
    abrirProduto(item) {
      if (!item?.id) return;

      this.$router.push({
        name: "produtosUpdate",
        params: { id_produto: item.id },
      });
    },
    formatNumber(value) {
      return new Intl.NumberFormat("pt-BR", {
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
    },
  },
};
</script>

<style scoped>
.display-4 {
  line-height: 1.1;
}

.produto-row {
  cursor: pointer;
}

.produto-row:hover {
  background-color: #f3f6f9;
}
</style>
